import { ShieldCheck } from "lucide-react";
import { ApiError } from "../../api/client.js";
import { useSetRole, useUser } from "../../api/hooks.js";
import type { Role } from "../../api/types.js";
import { Drawer } from "../../components/Drawer.js";
import {
  Avatar,
  CenteredSpinner,
  ErrorState,
  Label,
  Select,
} from "../../components/ui.js";
import { toast } from "../../components/toast.js";
import { GrantsEditor } from "./GrantsEditor.js";

export function UserDetailDrawer({
  userId,
  adminCount,
  onClose,
}: {
  userId: string;
  adminCount: number;
  onClose: () => void;
}) {
  const user = useUser(userId);
  const setRole = useSetRole(userId);

  const u = user.data;
  // Disable demoting the last remaining admin (backend also guards w/ LAST_ADMIN).
  const isLastAdmin = !!u && u.role === "ADMIN" && adminCount <= 1;

  async function changeRole(role: Role) {
    if (!u || role === u.role) return;
    try {
      await setRole.mutateAsync(role);
      toast("Role updated", "success");
    } catch (e) {
      if (e instanceof ApiError && e.code === "LAST_ADMIN") {
        toast("Cannot demote the last administrator", "error");
      }
      // other errors toasted globally
    }
  }

  return (
    <Drawer
      open
      onClose={onClose}
      title={
        u ? (
          <span className="flex items-center gap-2.5">
            <Avatar src={u.avatarUrl} name={u.displayName} className="h-8 w-8" />
            <span className="truncate">{u.displayName}</span>
            <span className="truncate text-sm font-normal text-muted-foreground">
              @{u.username}
            </span>
          </span>
        ) : (
          "User"
        )
      }
    >
      {user.isLoading && <CenteredSpinner />}
      {user.error && <ErrorState message={user.error.message} />}
      {u && (
        <div className="space-y-6">
          {/* Role */}
          <section>
            <Label>Role</Label>
            <Select
              value={u.role}
              disabled={setRole.isPending || isLastAdmin}
              onChange={(e) => changeRole(e.target.value as Role)}
              className="max-w-xs"
            >
              <option value="USER">USER</option>
              <option value="ADMIN">ADMIN</option>
            </Select>
            {isLastAdmin && (
              <p className="mt-1.5 flex items-center gap-1 text-xs text-amber-400">
                <ShieldCheck className="h-3.5 w-3.5" />
                Last administrator — cannot be demoted.
              </p>
            )}
          </section>

          {/* Identities */}
          <section>
            <Label>Linked identities</Label>
            {u.identities.length === 0 ? (
              <p className="text-xs text-muted-foreground">None</p>
            ) : (
              <ul className="space-y-1.5">
                {u.identities.map((id) => (
                  <li
                    key={`${id.provider}:${id.providerUserId}`}
                    className="flex items-center justify-between rounded-lg bg-card/50 px-3 py-2 text-sm ring-1 ring-inset ring-white/[0.06]"
                  >
                    <span className="font-medium capitalize text-foreground/90">
                      {id.provider}
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
                      {id.label ?? id.providerUserId}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Grants */}
          <section>
            <GrantsEditor userId={u.id} />
          </section>
        </div>
      )}
    </Drawer>
  );
}
